import { useMutation } from '@apollo/client';
import { GuideInput } from '__generated__/globalTypes';
import { CloneGuide, CloneGuideVariables } from 'graphql/mutation/__generated__/CloneGuide';
import { CreateGuide, CreateGuideVariables } from 'graphql/mutation/__generated__/createGuide';
import { DeleteGuide, DeleteGuideVariables } from 'graphql/mutation/__generated__/deleteGuide';
import { UpdateGuide, UpdateGuideVariables } from 'graphql/mutation/__generated__/updateGuide';
import {
    G_CLONE_GUIDE,
    G_CREATE_GUIDE,
    G_DELETE_GUIDE,
    G_UPDATE_GUIDE
} from 'graphql/mutation/guideMutation';
import { GuideQuery_guide } from 'graphql/query/__generated__/GuideQuery';
import { WORLD_MAP_INPUT_DEFAULT } from 'hooks/map/useCreateMap';
import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import { EToastType } from 'libs/types/site/IClient';
import { userMessage } from 'libs/utils/logic/ui/uiLogic';

export const DEFAULT_GUIDE_COLORS = {
    alfa0: '#000000',
    alfa1: '#2A2A2D',
    alfa2: '#313134',
    alfa3: '#3F3F42',
    beta0: '#FFFFFF',
    semi1: '#BEBEBE',
    accent1: '#7CA7FC',
    important1: '#F43434',
    support1: '#77FFC6',
    support2: '#F25B88',
    support3: '#5997F5',
    background: '#1A1A1D',
    textLight: '#fff',
    textSemi: '#BEBEBE',
    textDark: '#2A2A2D'
};

export const useGuideMutations = () => {
    const {
        getTokenForGuideId,
        storeTokenForMapId,
        storeTokenForGuideId,
        removeTokenForGuideId,
        removeTokenForMapId
    } = useWorldMapLocalStorage();
    const [createMutation] = useMutation<CreateGuide, CreateGuideVariables>(G_CREATE_GUIDE);
    const [cloneMutation] = useMutation<CloneGuide, CloneGuideVariables>(G_CLONE_GUIDE);
    const [updateMutation] = useMutation<UpdateGuide, UpdateGuideVariables>(G_UPDATE_GUIDE);
    const [deleteMutation] = useMutation<DeleteGuide, DeleteGuideVariables>(G_DELETE_GUIDE);

    async function create(guideInput?: GuideInput) {
        return createMutation({
            variables: {
                guideInput: guideInput ?? {
                    logoUrl:
                        'https://dvtqxks0zalev.cloudfront.net/a2fff6f7-0b85-4822-b0f9-c70dd458f5fb.png',
                    headerImageUrl:
                        'https://dvtqxks0zalev.cloudfront.net/9d0474cc-3ded-432e-8630-a61163a3fba0.png',
                    styleColors: Object.entries(DEFAULT_GUIDE_COLORS).map(([key, value]) => ({
                        colorId: key,
                        value
                    }))
                },
                worldMapInput: {
                    ...WORLD_MAP_INPUT_DEFAULT
                }
            },
            onCompleted: (result) => {
                const mapId = result.g_createGuide.map.worldMapId;
                const guideId = result.g_createGuide.guide.guideId;
                const token = result.g_createGuide.token;
                storeTokenForMapId(mapId, token);
                storeTokenForGuideId(guideId, token);
                return result;
            },
            onError: () => {
                userMessage(
                    EToastType.ERROR,
                    'Error while saving, please reload the page and try again.'
                );
            }
        });
    }

    async function clone(string: string) {
        return cloneMutation({
            variables: {
                guideId: string
            },
            onCompleted: (result) => {
                const mapId = result.g_cloneGuide.map.worldMapId;
                const guideId = result.g_cloneGuide.guide.guideId;
                const token = result.g_cloneGuide.token;
                storeTokenForMapId(mapId, token);
                storeTokenForGuideId(guideId, token);
                return result;
            },
            onError: () => {
                userMessage(
                    EToastType.ERROR,
                    'Error while saving, please reload the page and try again.'
                );
            }
        });
    }

    async function remove(guideId: string, mapId: string) {
        return deleteMutation({
            variables: {
                guideId,
                token: getTokenForGuideId(guideId)
            },
            onCompleted: (result) => {
                removeTokenForGuideId(mapId);
                removeTokenForMapId(guideId);
                return result;
            },
            update: (cache, { data }) => {
                if (data?.g_deleteGuide?.success) {
                    const removeId = cache.identify({
                        __typename: 'Guide',
                        guideId
                    });
                    cache.evict({ id: removeId });
                }
            },
            onError: () => {
                userMessage(
                    EToastType.ERROR,
                    'Error while saving, please reload the page and try again.'
                );
            }
        });
    }

    async function update(guideId: string, input: GuideInput, current: GuideQuery_guide) {
        function navigationToOptimisticResponse() {
            if (!input.navigation) {
                return null;
            }
        }

        return updateMutation({
            variables: {
                guideId,
                guideInput: input,
                token: getTokenForGuideId(guideId)
            },
            optimisticResponse: () => ({
                g_updateGuide: {
                    guide: {
                        guideId,
                        ...current,
                        ...input,
                        ...navigationToOptimisticResponse(),
                        __typename: 'Guide'
                    },
                    __typename: 'GuideResponse'
                }
            }),
            context: {
                debounceKey: guideId + '|' + Object.keys(input).join(';')
            },
            onError: () => {
                userMessage(
                    EToastType.ERROR,
                    'Error while saving, please reload the page and try again.'
                );
            }
        });
    }

    return {
        create,
        update,
        remove,
        clone
    };
};
